import React, { useState, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { Link } from "react-router-dom";
import { DocumentUploadCard } from "../ui/DocumentUploadCard";
import { UploadedFiles } from "../UploadedFiles";
import { CancelToReview } from "../CancelToReview";
import { InvalidRequirementReason } from "../InvalidRequirementReason";
import { Status } from "../../../redux/interfaces/folder.types";
import { toast } from "sonner";
import { uploadFileRequirement } from "../../../utils/vault/uploadFileRequirement";
import { useUploadFileToVault } from "../../../hooks/vault/mutations/useUploadFileToVault";
import { useUpdateVaultFormValues } from "../../../hooks/vault/mutations/useUpdateVaultFormValues";
import type { UploadFile } from "antd/es/upload/interface";
import { SIMPLE_DOCUMENT_MAX_FILE_SIZE_MB } from "../../../utils/vault/verificationUploadLimits";

interface Props {
  onRegisterSubmit?: (submitFn: (() => Promise<boolean>) | null) => void;
}

export const SATOpinion: React.FC<Props> = ({ onRegisterSubmit }) => {
  const [files, setFiles] = useState<UploadFile[]>([]);

  const dispatch = useAppDispatch();

  const { activeRequirement, activeVault, activeTab } = useAppSelector(
    (state) => state.vault,
  );
  const uploadMutation = useUploadFileToVault(activeVault?.id);
  const updateFormValuesMutation = useUpdateVaultFormValues(activeVault?.id);

  const enabled =
    activeRequirement?.status === Status.PendingUpload ||
    activeRequirement?.status === Status.Invalid;

  // Reset files when requirement changes
  useEffect(() => {
    setFiles([]);
  }, [activeRequirement?.id]);

  const handleSubmit = async (): Promise<boolean> => {
    if (files.length === 0) {
      toast.error("Por favor sube un archivo");
      return false;
    }

    const file = files[0]?.originFileObj;
    const formFiles = [file];
    const documentType = "sat_opinion";
    const requirementFiles = activeRequirement?.files || [];
    const formNFiles = 1;
    const successMessage = "Opinión del SAT subida correctamente.";

    const messageApi = {
      success: (msg: string) => toast.success(msg),
      error: (msg: string) => toast.error(msg),
    };

    try {
      await uploadFileRequirement({
        uploadMutateAsync: uploadMutation.mutateAsync,
        updateFormValuesMutateAsync: updateFormValuesMutation.mutateAsync,
        dispatch,
        requirementFiles,
        documentType,
        formNFiles,
        formFiles,
        formValues: {},
        activeRequirement,
        activeVault,
        activeTab,
        successMessage,
        messageApi,
      });
      setFiles([]);
      return true;
      setFiles([]);
      return true;
    } catch (error) {
      console.error("Upload failed:", error);
      return false;
    }
  };

  // Register submit function with parent for external triggering
  const submitFnRef = useRef<(() => Promise<boolean>) | undefined>(undefined);
  submitFnRef.current = handleSubmit;

  useEffect(() => {
    if (!onRegisterSubmit) return;
    const fn = async () => submitFnRef.current?.() ?? false;
    onRegisterSubmit(fn);
    return () => onRegisterSubmit(null);
  }, [onRegisterSubmit]);

  return (
    <div className="w-full">
      {/* Description */}
      <p className="text-gray-600 mb-8">
        Adjunta la opinión de cumplimiento de obligaciones fiscales del SAT de
        la <span className="font-semibold text-gray-900">empresa</span>, no
        mayor a 3 meses.
      </p>

      {/* Invalid Requirement Reason — shown at the top so the rejection
          message is visible before the user scrolls through the form. */}
      <InvalidRequirementReason requirement={activeRequirement!} />

      {/* Two column layout */}
      <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-6 md:gap-8">
        {/* Left Column - Info */}
        <div className="space-y-6 md:sticky md:top-0 md:self-start">
          <img
            src="/sat-logo.png"
            alt="Logotipo del SAT"
            className="w-6/12 sm:w-3/12 mb-4"
          />

          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Instrucciones
            </p>
            <p className="text-sm text-gray-600">
              Obtenlo aquí:{" "}
              <Link
                to="https://bit.ly/3isVsRo"
                className="text-blue-600 hover:underline"
                target="_blank"
              >
                https://bit.ly/3isVsRo
              </Link>
            </p>
          </div>
        </div>

        {/* Right Column - File Upload */}
        <div className="space-y-6">
          <div className="w-full">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Opinión del SAT
              </span>
            </div>
            <DocumentUploadCard
              name="sat_opinion"
              label="Opinión de cumplimiento del SAT"
              type="both"
              enableCropper={false}
              maxSizeMB={SIMPLE_DOCUMENT_MAX_FILE_SIZE_MB}
              onFileChange={setFiles}
              value={files}
            />
          </div>

          {/* Uploaded Files */}
          <UploadedFiles />

          {/* Cancel to review (when already submitted) */}
          {!enabled && activeRequirement?.status === Status.PendingReview && (
            <CancelToReview requirement={activeRequirement} />
          )}
        </div>
      </div>
    </div>
  );
};
